import {
  Autocomplete,
  AutocompleteRenderGroupParams,
  AutocompleteRenderInputParams,
  InputAdornment,
  Stack,
  TextField,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { DefaultTheme, styled, useTheme } from "@mui/system";
import clsx from "clsx";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { AutocompleteHighlight } from "../../../shared/AutocompleteHighlight";
import { ValidationMessage } from "../../../shared/ValidationMessage";
import { Asset } from "../../../shared/interfaces/Asset";
import { getAssetTypeLabel } from "../../../shared/constants/managedSurveyAssetTypes";

interface SingleSelectAssetDropdownProps {
  onAssetChange: (selectedAssets: Asset[]) => void;
  width?: string;
  invalid?: boolean;
  invalidMessage: string;
  disabled?: boolean;
  selectedAsset?: Asset | undefined;
  options: Asset[];
  disableErrorSpace?: boolean;
}

const GroupHeader = styled("div")(({ theme }) => ({
  padding: "4px 10px",
  color: theme.palette.primary.main,
  backgroundColor: theme.palette.primary.contrastText,
}));

const GroupItems = styled("ul")({
  padding: 0,
});

const useStyles = makeStyles((theme: DefaultTheme) => ({
  dropdownIcon: {
    color: theme.palette.midGrey.main,
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    height: "100%",
    alignItems: "right",
  },
}));

export default function SingleSelectAssetDropdown(props: SingleSelectAssetDropdownProps) {
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();

  const handleChange = (_event: React.SyntheticEvent, newValue: Asset | null) => {
    props.onAssetChange(newValue ? [newValue] : []);
  };

  const [open, setOpen] = useState(false);

  return (
    <Stack
      direction="column"
      sx={{
        width: props.width,
        minHeight: props.disableErrorSpace ? "calc(100%)" : "calc(100% + 20px)",
        paddingTop: props.disableErrorSpace ? "0px" : "20px",
      }}
    >
      <div
        onClick={() => {
          setOpen(!open);
        }}
      >
        <Autocomplete
          autoHighlight
          size="small"
          blurOnSelect
          open={open}
          onClose={() => setOpen(false)}
          options={props.options}
          groupBy={(option: Asset) => option.assetTypeKey}
          getOptionLabel={(option: Asset) => option.name}
          onChange={handleChange}
          value={props.selectedAsset ?? null}
          isOptionEqualToValue={(option: Asset, value: Asset) => option.id === value.id}
          sx={{ width: props.width || 200, backgroundColor: theme.palette.primary.contrastText }}
          renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: Asset, { inputValue }) => (
            <AutocompleteHighlight renderProps={props} optionLabel={option.name} inputValue={inputValue} />
          )}
          renderInput={(params: AutocompleteRenderInputParams) => (
            <TextField
              {...params}
              sx={{
                input: {
                  color: theme.palette.grey.dark,
                  "&::placeholder": {
                    opacity: 1,
                  },
                },
                label: { color: theme.palette.blue.main },
              }}
              placeholder={"Select " + t("asset.asset")}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <InputAdornment position="end" style={{ alignItems: "center", height: 16 }}>
                    <i className={clsx("pi pi-chevron-down", classes.dropdownIcon)} />
                  </InputAdornment>
                ),
                style: { paddingRight: 8 },
              }}
            />
          )}
          renderGroup={(params: AutocompleteRenderGroupParams) => (
            <li key={params.key}>
              <GroupHeader>{t(`assetTypes.${getAssetTypeLabel(params.group)}`)}</GroupHeader>
              <GroupItems>{params.children}</GroupItems>
            </li>
          )}
        />
      </div>
      {
        <div style={{ height: props.disableErrorSpace ? "0px" : "20px" }}>
          {props.invalid && props.invalidMessage && (
            <ValidationMessage invalidMessage={props.invalidMessage} validationType="error" showIcon />
          )}
        </div>
      }
    </Stack>
  );
}
